import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

// מפתח שמירה ב-localStorage לכל סיור, כדי שיוצג פעם אחת בלבד
const tourStorageKey = (tourName: string) => `tour_completed_${tourName}`;

export const TOUR_NAMES = ['home', 'reading'] as const;

export const wasTourCompleted = (tourName: string) => !!localStorage.getItem(tourStorageKey(tourName));

const markTourCompleted = (tourName: string) => localStorage.setItem(tourStorageKey(tourName), 'true');

// רישום הסיורים שמותקנים כרגע על הדף, כדי שאפשר יהיה להפעיל אותם מחדש
// מתפריט ההמבורגר גם בלי לנווט או לרענן את הדף
const activeTours = new Map<string, () => void>();

/**
 * מאפס את הסימון "הסיור הושלם" ומריץ מחדש את הסיור שמותקן על הדף הנוכחי.
 * מחובר לפריט "צפייה בהדרכה שוב" בתפריט.
 */
export function restartOnboardingTours() {
  TOUR_NAMES.forEach((name) => localStorage.removeItem(tourStorageKey(name)));
  activeTours.forEach((start) => start());
}

/**
 * מציג סיור הדרכה חד-פעמי (driver.js) כשהתנאי enabled מתקיים.
 * הסיור מסומן כהושלם גם אם המשתמש סגר אותו באמצע - כדי לא להציק שוב,
 * ותמיד אפשר להחזיר אותו מהתפריט.
 */
function useOnboardingTour(
  tourName: string,
  enabled: boolean,
  buildSteps: () => DriveStep[],
  onFinished?: () => void
) {
  const { t } = useTranslation();

  // הערכים נקראים דרך ref כדי ש-start יישאר יציב ולא יריץ את הסיור מחדש בכל רינדור
  const enabledRef = useRef(enabled);
  const buildStepsRef = useRef(buildSteps);
  const onFinishedRef = useRef(onFinished);
  const tRef = useRef(t);
  enabledRef.current = enabled;
  buildStepsRef.current = buildSteps;
  onFinishedRef.current = onFinished;
  tRef.current = t;

  const tourRef = useRef<ReturnType<typeof driver> | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const start = useCallback(() => {
    if (!enabledRef.current) {
      return;
    }

    clearTimeout(timeoutRef.current);

    // השהיה קצרה כדי לתת לפריסת הדף להתייצב לפני מדידת האלמנטים
    timeoutRef.current = setTimeout(() => {
      const translate = tRef.current;

      tourRef.current?.destroy();
      tourRef.current = driver({
        showProgress: true,
        // i18next משתמש גם הוא בתחביר {{...}}, ולכן המחרוזת מגיעה עם %current%/%total%
        // ורק כאן מומרת לתחביר של driver.js
        progressText: translate('tourProgress').replace('%current%', '{{current}}').replace('%total%', '{{total}}'),
        nextBtnText: translate('tourNext'),
        prevBtnText: translate('tourBack'),
        doneBtnText: translate('tourDone'),
        onDestroyed: () => {
          markTourCompleted(tourName);
          onFinishedRef.current?.();
        },
        allowKeyboardControl: true,
        // driver.js כבר שם role="dialog" על החלון; קורא מסך צריך לדעת שהוא מודאלי
        onPopoverRender: (popover) => {
          popover.wrapper.setAttribute('aria-modal', 'true');
        },
        // נעילת הסיור: המשתמש לא יכול ללחוץ על האלמנט המודגש או על הרקע ולצאת
        // באמצע הסיור. היציאה היחידה היא כפתור ה-X או מקש Escape,
        // ותמיד אפשר להחזיר את הסיור מהתפריט.
        disableActiveInteraction: true,
        overlayClickBehavior: 'nextStep',
        steps: buildStepsRef.current(),
      });
      tourRef.current.drive();
    }, 800);
  }, [tourName]);

  useEffect(() => {
    if (enabled && !wasTourCompleted(tourName)) {
      start();
    }
  }, [enabled, tourName, start]);

  // רישום להפעלה מחדש מהתפריט + ניקוי במעבר דף
  useEffect(() => {
    activeTours.set(tourName, start);

    return () => {
      activeTours.delete(tourName);
      clearTimeout(timeoutRef.current);
      tourRef.current?.destroy();
      tourRef.current = undefined;
    };
  }, [tourName, start]);
}

// הטקסטים מנוסחים בלשון תיאור ולא בלשון הוראה ("אפשר לבחור" ולא "בחרו עכשיו"),
// כדי שהמשתמש לא ינסה לבצע פעולה באמצע הסיור. יחד עם disableActiveInteraction
// זה מבטיח שהמשתמש רואה את כל הצעדים לפי הסדר.

/** סיור דף הבית: ברוכים הבאים, בחירת ספר, בחירת קטע והתחלה */
export function useHomeOnboardingTour(enabled: boolean, onFinished?: () => void) {
  const { t } = useTranslation();

  useOnboardingTour(
    'home',
    enabled,
    () => [
      {
        element: '[data-tour="selection-card"]',
        popover: { title: t('tourHomeCardTitle'), description: t('tourHomeCardDescription') },
      },
      {
        element: '[data-tour="book-selector"]',
        popover: { title: t('tourHomeBookTitle'), description: t('tourHomeBookDescription') },
      },
      {
        element: '.levels-selectors',
        popover: { title: t('tourHomeSelectorsTitle'), description: t('tourHomeSelectorsDescription') },
      },
      {
        element: '[data-tour="start-button"]',
        popover: { title: t('tourHomeStartTitle'), description: t('tourHomeStartDescription') },
      },
    ],
    onFinished
  );
}

/** סיור דף השינון: שני מצבי התרגול - קריאה מול הטקסט ושינון בעל פה */
export function useReadingOnboardingTour(enabled: boolean) {
  const { t } = useTranslation();

  useOnboardingTour('reading', enabled, () => [
    {
      element: '[data-tour="mode-written"]',
      popover: { title: t('tourReadingWrittenTitle'), description: t('tourReadingWrittenDescription') },
    },
    {
      element: '[data-tour="mode-byHeart"]',
      popover: { title: t('tourReadingByHeartTitle'), description: t('tourReadingByHeartDescription') },
    },
    // מקרא הצבעים: המקרא עצמו מוצג מעל, כאן רק מה שהוא לא מראה (כמעט ונבלעה נחשבות כנקראו)
    {
      element: '[data-tour="word-key"]',
      popover: { title: t('tourReadingColorsTitle'), description: t('tourReadingColorsDescription') },
    },
    // כרטיס בלי element - driver.js מציג אותו במרכז המסך. הזרימה הרציפה היא
    // הסבר על אופן התרגול ולא כפתור שאפשר להצביע עליו.
    {
      popover: { title: t('tourReadingFlowTitle'), description: t('tourReadingFlowDescription') },
    },
  ]);
}
